import { Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from '@mui/material';

interface CounterRow {
    upfront: number;
    splitNeeded: number; // -1 = impossible even at 100%
    backend24: number;
    total: number;
}

interface CounterOfferCardProps {
    standardFee: number;
    counterRows: CounterRow[];
    proposedUpfront: number;
    proposedSplit: number;
}

export const CounterOfferCard = ({
    standardFee,
    counterRows,
    proposedUpfront,
    proposedSplit,
}: CounterOfferCardProps) => {
    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Counter Offers
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                    Your standard fee is ${standardFee.toLocaleString()}. What split covers the gap at each upfront:
                </Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Upfront</TableCell>
                                <TableCell align="right">Gap to Cover</TableCell>
                                <TableCell align="right">Split Needed</TableCell>
                                <TableCell align="right">24mo Backend</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {counterRows.map((row) => {
                                const isProposed = row.upfront === proposedUpfront;
                                const gap = Math.max(0, standardFee - row.upfront);
                                const impossible = row.splitNeeded === -1;
                                const noSplitNeeded = gap <= 0;
                                return (
                                    <TableRow
                                        key={row.upfront}
                                        sx={isProposed ? { bgcolor: 'action.selected' } : undefined}
                                    >
                                        <TableCell>
                                            ${row.upfront.toLocaleString()}
                                            {isProposed && (
                                                <Chip label="Their offer" size="small" sx={{ ml: 1 }} />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {noSplitNeeded ? '—' : `$${gap.toLocaleString()}`}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: impossible ? 'error.main' : noSplitNeeded ? 'success.main' : 'text.primary',
                                            }}
                                        >
                                            {noSplitNeeded
                                                ? '0% (full rate paid)'
                                                : impossible
                                                    ? 'Not possible'
                                                    : `${row.splitNeeded}%`
                                            }
                                        </TableCell>
                                        <TableCell align="right">
                                            {noSplitNeeded
                                                ? '—'
                                                : impossible
                                                    ? `$${row.backend24.toLocaleString()} (max at 100%)`
                                                    : `$${row.backend24.toLocaleString()}`
                                            }
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};
